import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import Header from "@/components/Header";
import ViewSelector from "@/components/ViewSelector";
import QuickAdd from "@/components/QuickAdd";
import DaysTimeline from "@/components/DaysTimeline";
import ItineraryItem from "@/components/ItineraryItem";
import { TripComponent, type ComponentType } from "@shared/schema";
import DetailedViewModal from "@/components/DetailedViewModal";
import AddComponentModal from "@/components/AddComponentModal";
import AIPlannerSheet from "@/components/AIPlannerSheet";
import QuickAIPlanner from "@/components/QuickAIPlanner";
import TripFormDialog from "@/components/TripFormDialog";
import TripAssistantSidebar from "@/components/TripAssistantSidebar";
import TripAssistantColumn from "@/components/TripAssistantColumn";
import { ProposalList } from "@/components/ProposalList";
import { PdfImportDialog } from "@/components/PdfImportDialog";
import { formatDateRange, getDaysBetweenDates, formatDay } from "@/lib/utils";
import { addDays, format, parse } from "date-fns";
import { ViewType } from "@/lib/types";
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  Sparkles, 
  MessageSquareText, 
  MapPinIcon, 
  Bot, 
  ChevronLeft, 
  ChevronRight,
  MessageSquare,
  Plane as PlaneIcon, 
  Building as BuildingIcon, 
  Smile as SmileIcon, 
  Truck as TruckIcon, 
  Utensils as UtensilsIcon, 
  Plus as PlusIcon,
  ChevronDown,
  Ship,
  Shield,
  Package
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTrip } from "@/hooks/useTrip";
import { Card, CardContent } from "@/components/ui/card";
import { Client, ClientTrip, type TripPaymentStatus } from "@shared/schema";
import { ensureComponentType } from "@/lib/componentTypes";

export default function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const tripId = parseInt(id);
  const { toast } = useToast();
  const [debugOpen, setDebugOpen] = useState(false);
  
  // Handle invalid trip ID
  if (!id || isNaN(tripId)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Trip Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The trip ID is invalid or missing.</p>
          <Button onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  const [viewType, setViewType] = useState<ViewType>("timeline");
  const [selectedComponent, setSelectedComponent] = useState<TripComponent | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false);
  const [addComponentType, setAddComponentType] = useState<ComponentType | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [visibleDayRange, setVisibleDayRange] = useState<[number, number]>([0, 2]); // showing only 3 day columns (plus AI column = 4 total)
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState("");
  const [isAssistantOpen, setIsAssistantOpen] = useState(true); // AI panel expanded by default
  const [isDestinationModalOpen, setIsDestinationModalOpen] = useState(false);
  const [editingDayDestination, setEditingDayDestination] = useState<{ id?: number; dayIndex: number; destination: string; notes: string | null } | null>(null);
  const [hideMeals, setHideMeals] = useState<boolean>(false);
  const [isPdfImportOpen, setIsPdfImportOpen] = useState(false);
  
  // Enhanced scroll prevention for view changes
  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    
    // Create a function that will force scroll to top
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
    };
    
    // Apply multiple scroll resets with different timings to catch any delayed rendering
    const timers = [
      setTimeout(forceScrollToTop, 50),
      setTimeout(forceScrollToTop, 100),
      setTimeout(forceScrollToTop, 200),
      setTimeout(forceScrollToTop, 500),
      setTimeout(forceScrollToTop, 1000)
    ];
    
    // Also force scroll on resize
    window.addEventListener('resize', forceScrollToTop);
    
    // Clean up all timers and event listeners
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      window.removeEventListener('resize', forceScrollToTop);
    };
  }, [viewType]);
  
  const { 
    trip, 
    components, 
    isLoading, 
    groupedByDay,
    dateRange,
    hasTripDates,
    dayCount,
    dayLocations,
    dayDestinations,
    invoiceSummary,
  } = useTrip(tripId);
  
  // Fetch contacts for this trip
  const { data: tripContacts = [] } = useQuery<Client[]>({
    queryKey: [`/api/trips/${tripId}/contacts`],
    enabled: !!trip,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch contact-trip associations for this trip
  const { data: contactTrips = [] } = useQuery<ClientTrip[]>({
    queryKey: [`/api/trips/${tripId}/contact-trips`],
    enabled: !!trip,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  const { data: tripThreads = [] } = useQuery<any[]>({
    queryKey: [`/api/trips/${tripId}/threads`],
    enabled: !!trip,
    staleTime: 60 * 1000,
  });
  
  // Helper functions to refetch data
  const refetchTrip = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
  };
  
  const refetchComponents = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
  };

  const handleComponentClick = (component: TripComponent) => {
    setSelectedComponent(component);
    setIsDetailModalOpen(true);
  };

  const handleAddComponent = (type: ComponentType, dayIndex?: number) => {
    setAddComponentType(type);
    if (dayIndex !== undefined) {
      setSelectedDayIndex(dayIndex);
    }
    setIsAddModalOpen(true);
  };

  const handleDeleteComponent = async (id: number) => {
    try {
      await fetch(`/api/components/${id}`, {
        method: "DELETE",
      });
      
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      setIsDetailModalOpen(false);
      toast({
        title: "Success",
        description: "Component deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete component",
        variant: "destructive",
      });
    }
  };
  
  const handleRegenerateComponent = async (component: TripComponent, prompt: string) => {
    try {
      const response = await fetch(`/api/trips/${tripId}/replace-component`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          component,
          prompt,
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to regenerate component");
      }
      
      // Get the new component data
      const data = await response.json();
      
      // Create the regenerated component in the database
      const createResponse = await fetch("/api/components", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.component),
      });
      
      if (!createResponse.ok) {
        throw new Error("Failed to save regenerated component");
      }
      
      // Delete the old component
      await fetch(`/api/components/${component.id}`, {
        method: "DELETE",
      });
      
      // Refresh the component list
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
      
      toast({
        title: "Success",
        description: "Component has been regenerated",
      });
    } catch (error) {
      console.error("Error regenerating component:", error);
      toast({
        title: "Error",
        description: "Failed to regenerate component",
        variant: "destructive",
      });
    }
  };

  const navigateDateRange = (direction: 'prev' | 'next') => {
    const [start, end] = visibleDayRange;
    if (direction === 'prev') {
      // Don't allow negative day indexes
      if (start > 0) {
        setVisibleDayRange([start - 1, end - 1]);
      }
    } else if (direction === 'next') {
      // Don't allow navigating past the end of the trip
      if (start + 1 < dayCount) {
        setVisibleDayRange([start + 1, end + 1]);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header tripName="Loading..." />
        <div className="px-6 py-2 text-xs text-gray-500">
          Loading trip {tripId} …
        </div>
        <main className="container mx-auto px-4 py-6">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-96 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold mb-4">Trip Not Found</h2>
          <p className="mb-4">The trip you're looking for doesn't exist or has been deleted.</p>
          <div className="mt-4 text-xs text-gray-500">Tried ID: {tripId}</div>
          <Button onClick={() => window.location.href = "/"}>
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  const handleEditTrip = () => {
    setIsEditTripModalOpen(true);
  };

  // Functions to handle day destinations
  const handleOpenDestinationModal = (dayIndex: number) => {
    // Check if there's an existing destination for this day
    const existingDestination = dayDestinations?.find(dest => dest.dayIndex === dayIndex);
    
    if (existingDestination) {
      setEditingDayDestination({
        id: existingDestination.id,
        dayIndex: existingDestination.dayIndex,
        destination: existingDestination.destination,
        notes: existingDestination.notes
      });
    } else {
      setEditingDayDestination({
        dayIndex,
        destination: '',
        notes: null
      });
    }
    
    setIsDestinationModalOpen(true);
  };

  const handleSaveDayDestination = async () => {
    if (!editingDayDestination) return;
    
    try {
      if (editingDayDestination.id) {
        // Update existing day destination
        const response = await fetch(`/api/day-destinations/${editingDayDestination.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            destination: editingDayDestination.destination,
            notes: editingDayDestination.notes
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to update day destination');
        }
      } else {
        // Create new day destination
        const response = await fetch(`/api/trips/${tripId}/day-destinations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tripId,
            dayIndex: editingDayDestination.dayIndex,
            destination: editingDayDestination.destination,
            notes: editingDayDestination.notes
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to create day destination');
        }
      }
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
      
      // Close the modal
      setIsDestinationModalOpen(false);
      setEditingDayDestination(null);
      
      toast({
        title: 'Success',
        description: `Day destination ${editingDayDestination.id ? 'updated' : 'created'} successfully`,
      });
    } catch (error) {
      console.error('Error saving day destination:', error);
      toast({
        title: 'Error',
        description: `Failed to ${editingDayDestination.id ? 'update' : 'create'} day destination`,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteDayDestination = async () => {
    if (!editingDayDestination?.id) return;
    
    try {
      const response = await fetch(`/api/day-destinations/${editingDayDestination.id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete day destination');
      }
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
      
      // Close the modal
      setIsDestinationModalOpen(false);
      setEditingDayDestination(null);
      
      toast({
        title: 'Success',
        description: 'Day destination deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting day destination:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete day destination',
        variant: 'destructive',
      });
    }
  };
  
  // With our new layout, we're showing all days automatically in the grid,
  // so we don't need to adjust the visible day range when toggling the assistant
  
  // Helper function to filter components based on meal visibility setting
  const filterComponents = (components: TripComponent[]) => {
    if (!hideMeals) return components;
    
    // Filter out components of type "food" and those with "breakfast", "lunch", or "dinner" in the title
    return components.filter(component => {
      // Check component type
      if (component.type === "food") return false;
      
      // Check title for meal keywords (case insensitive)
      const title = component.title.toLowerCase();
      if (title.includes("breakfast") || title.includes("lunch") || title.includes("dinner") || title.includes("meal")) {
        return false;
      }
      
      return true;
    });
  };
  
  // Quick add items for the top toolbar
  interface QuickAddItem {
    type: string;
    label: string;
    icon: React.ReactNode;
    bgClass: string;
  }
  
  const quickAddItems: QuickAddItem[] = [
    { 
      type: "flight", 
      label: "Flight", 
      icon: <PlaneIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-orange-400 to-amber-500 text-white"
    },
    { 
      type: "cruise", 
      label: "Cruise", 
      icon: <Ship className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
    },
    { 
      type: "tour", 
      label: "Tour", 
      icon: <Package className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
    },
    { 
      type: "package", 
      label: "Package", 
      icon: <Package className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-purple-500 to-purple-600 text-white"
    },
    { 
      type: "hotel", 
      label: "Hotel", 
      icon: <BuildingIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-indigo-500 to-purple-500 text-white" 
    },
    { 
      type: "travel-insurance", 
      label: "Travel Insurance", 
      icon: <Shield className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-green-500 to-green-600 text-white"
    },
    { 
      type: "activity", 
      label: "Activity", 
      icon: <SmileIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-amber-400 to-orange-400 text-white" 
    },
    { 
      type: "transport", 
      label: "Transport", 
      icon: <TruckIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-slate-500 to-slate-600 text-white" 
    },
    { 
      type: "food", 
      label: "Meal", 
      icon: <UtensilsIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-rose-400 to-pink-500 text-white" 
    },
    { 
      type: "pdf-import", 
      label: "Import PDF", 
      icon: (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          <path d="M10.5,10.5H12.5V12.5H10.5V10.5M10.5,13.5H12.5V15.5H10.5V13.5M7.5,10.5H9.5V12.5H7.5V10.5M7.5,13.5H9.5V15.5H7.5V13.5" />
        </svg>
      ), 
      bgClass: "bg-gradient-to-r from-red-500 to-red-600 text-white" 
    },
    { 
      type: "other", 
      label: "Other", 
      icon: <PlusIcon className="h-4 w-4" />, 
      bgClass: "bg-gradient-to-r from-sky-400 to-blue-500 text-white" 
    },
  ];

  // Handle quick add item clicks
  const handleQuickAddClick = (type: string) => {
    if (type === "pdf-import") {
      setIsPdfImportOpen(true);
      return;
    }

    handleAddComponent(ensureComponentType(type));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        tripName={trip.name}
        onEditTrip={handleEditTrip}
        dateRange={dateRange}
        tripId={tripId}
        recordLocator={trip.recordLocator ?? null}
        contacts={tripContacts}
        contactTrips={contactTrips}
        tripStatus={invoiceSummary?.paymentStatus ?? (trip.tripStatus as TripPaymentStatus) ?? null}
        hasDerivedDates={!hasTripDates}
        travelers={invoiceSummary?.travelers}
      />
      {/* Inline debug toggle */}
      <div className="px-8 mt-2">
        <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline" onClick={() => setDebugOpen(!debugOpen)}>
          {debugOpen ? 'Hide debug' : 'Show debug'}
        </button>
        {debugOpen && (
          <pre className="mt-2 text-[10px] bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded overflow-auto max-h-64 text-gray-900 dark:text-gray-100">
{JSON.stringify({
  tripId,
  hasTrip: !!trip,
  componentsCount: components?.length,
  dayDestinationsCount: dayDestinations?.length,
  dayCount,
  dateRange,
}, null, 2)}
          </pre>
        )}
      </div>
      
      <main className="w-full py-3 px-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <ViewSelector 
              activeView={viewType} 
              onChange={(newViewType) => {
                // If switching to timeline view, reset to show multiple days
                if (newViewType === "timeline" && viewType !== "timeline") {
                  // For timeline view, show 3 days (plus AI card = 4 columns total)
                  setVisibleDayRange([0, 2]); 
                }
                
                // Update view type
                setViewType(newViewType);
              }} 
            />
            
            {/* Hide Meals Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="hide-meals"
                checked={hideMeals}
                onCheckedChange={setHideMeals}
              />
              <Label htmlFor="hide-meals" className="text-sm cursor-pointer">
                Hide Meals
              </Label>
            </div>
          </div>
          
          <div className="flex items-center">
            {/* Quick Add dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  className="flex items-center gap-2 mr-4"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Quick Add</span>
                  <ChevronDown className="h-3 w-3 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {quickAddItems.map((item, index) => (
                  <div key={item.type}>
                    <DropdownMenuItem 
                      className="flex items-center gap-3 cursor-pointer py-3"
                      onClick={() => handleQuickAddClick(item.type)}
                    >
                      <div className={`w-8 h-8 ${item.bgClass} rounded-full flex items-center justify-center shadow-sm flex-shrink-0`}>
                        {item.icon}
                      </div>
                      <span className="font-medium">{item.label}</span>
                    </DropdownMenuItem>
                    {/* Add separator after PDF import */}
                    {item.type === "pdf-import" && index < quickAddItems.length - 1 && (
                      <DropdownMenuSeparator />
                    )}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Days navigation controls - arrows on the right */}
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="icon" aria-label="Previous dates"
                onClick={() => navigateDateRange('prev')}
                disabled={visibleDayRange[0] <= 0}
                className="h-8 w-8"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" aria-label="Next dates"
                onClick={() => navigateDateRange('next')}
                disabled={visibleDayRange[0] + 1 >= dayCount}
                className="h-8 w-8"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Linked Threads</h3>
              </div>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{tripThreads.length} linked</span>
            </div>
            {tripThreads.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">No threads linked to this trip yet.</p>
                <p className="text-xs text-gray-500 mt-1">Link threads from the Threads workspace to see them here.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                {tripThreads.map((thread: any) => (
                  <div
                    key={thread.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{thread.title}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Updated {format(new Date(thread.updatedAt), 'PPpp')} • {thread.provider} / {thread.model}
                        </p>
                      </div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        Linked {format(new Date(thread.linkedAt || thread.updatedAt), 'PPpp')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {viewType === "timeline" && (
          <>
            
            {/* Fixed layout with AI panel on left and day columns on right */}
            <div className="flex gap-4 overflow-hidden">
              {/* Fixed AI Assistant panel on the left */}
              {trip && components && (
                <div 
                  className={`h-[calc(100vh-250px)] border border-gray-200 rounded-lg transition-all duration-300 flex-shrink-0 sticky ${
                    isAssistantOpen ? "w-[350px]" : "w-[50px]"
                  }`}
                  style={{ maxHeight: 'calc(100vh - 200px)', marginTop: 0 }}
                >
                  <div className="border-b border-gray-200 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                        <Bot className="h-5 w-5 flex-shrink-0 text-primary" />
                        {isAssistantOpen && <h3 className="font-medium text-primary">Trip Assistant</h3>}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon" aria-label={isAssistantOpen ? "Collapse Trip Assistant" : "Expand Trip Assistant"}
                        className="h-8 w-8 text-gray-500 hover:bg-gray-100 flex-shrink-0"
                        onClick={() => setIsAssistantOpen(!isAssistantOpen)}
                      >
                        {isAssistantOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  {isAssistantOpen ? (
                    <div className="flex-1 h-[calc(100%-48px)] overflow-auto">
                      <TripAssistantColumn
                        key={trip.id}
                        trip={trip}
                        components={components}
                        contacts={tripContacts}
                        onSuccess={() => {
                          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                        }}
                        onTripUpdated={() => {
                          // Reload everything when trip details change
                          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
                          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
                        }}
                      />
                    </div>
                  ) : (
                    <div className="p-2 text-center">
                      <MessageSquare className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    </div>
                  )}
                </div>
              )}
              
              {/* Day columns container - responsive grid */}
              <div className="grid gap-4 flex-grow" 
                   style={{ 
                     gridTemplateColumns: `repeat(auto-fit, minmax(225px, 1fr))`,
                     minWidth: 0, // Ensures flex item can shrink below min-content width
                     maxWidth: '100%' // Prevent overflow
                   }}>
                {/* Show days based on visibleDayRange, as many as will fit */}
                {Array.from(
                  { length: Math.min(dayCount - visibleDayRange[0], 10) }, // Show up to 10 days maximum, but limit by remaining days
                  (_, i) => {
                    const dayIndex = visibleDayRange[0] + i;
                    // Skip if this day doesn't exist
                    if (dayIndex >= dayCount) return null;
                    
                    const dayDate = addDays(new Date(dateRange[0]), dayIndex);
                    // Apply meal filtering to components
                    const components = filterComponents(groupedByDay[dayIndex] || []);
                    const dayNumber = dayIndex + 1;
                    const formattedDate = format(dayDate, "MMM d");
                    const locations = dayLocations[dayIndex] || [];
                    
                    return (
                      <div key={dayIndex} className="overflow-hidden border rounded-lg shadow-sm">
                        <div 
                          className="bg-primary text-white p-3 cursor-pointer hover:bg-primary/90 transition-colors"
                          onClick={() => {
                            // Update view and day range
                            setViewType("daily");
                            setVisibleDayRange([dayIndex, dayIndex]);
                            // We don't need to manually scroll here as the useEffect will handle it
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-bold">Day {dayNumber}</h3>
                            <p className="text-sm opacity-90">{formattedDate}</p>
                          </div>
                          
                          {/* Location indicator with edit button */}
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-1">
                              <MapPinIcon className="h-3 w-3" />
                              <span className="text-[14px] font-medium">
                                {locations.length > 0 ? (
                                  locations[0]
                                ) : (
                                  "No destination set"
                                )}
                              </span>
                            </div>
                            <button 
                              className="text-xs text-white/80 hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDestinationModal(dayIndex);
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                        
                        <div className="p-3 space-y-3 max-h-[500px] overflow-y-auto">
                          {components.length > 0 ? (
                            components
                              .sort((a, b) => {
                                if (!a.startTime) return 1;
                                if (!b.startTime) return -1;
                                return a.startTime.localeCompare(b.startTime);
                              })
                              .map((component) => (
                                <ItineraryItem
                                  key={component.id}
                                  component={component}
                                  onClick={() => handleComponentClick(component)}
                                  onDelete={() => handleDeleteComponent(component.id)}
                                  onEdit={() => {
                                    setSelectedComponent(component);
            setAddComponentType(ensureComponentType(component.type));
                                    setSelectedDayIndex(component.dayIndex);
                                    setIsAddModalOpen(true);
                                  }}
                                  onRegenerate={() => {
                                    setSelectedComponent(component);
                                    setRegeneratePrompt(
                                      `Please suggest a better ${component.type} for day ${component.dayIndex + 1} of my trip.`
                                    );
                                    setIsRegenerateModalOpen(true);
                                  }}
                                />
                              ))
                          ) : (
                            <div className="text-center py-4">
                              <p className="text-sm text-gray-500 mb-2">No activities yet</p>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleAddComponent('other', dayIndex)}
                              >
                                Add
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </>
        )}
        
        {viewType === "daily" && (
          <div className="flex gap-6 overflow-hidden">
            {/* Fixed AI Assistant panel on the left */}
            {trip && components && (
              <div 
                className={`h-[calc(100vh-250px)] border border-gray-200 rounded-lg transition-all duration-300 flex-shrink-0 sticky ${
                  isAssistantOpen ? "w-[350px]" : "w-[50px]"
                }`}
                style={{ maxHeight: 'calc(100vh - 200px)', marginTop: 0 }}
              >
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                      <Bot className="h-5 w-5 flex-shrink-0 text-primary" />
                      {isAssistantOpen && <h3 className="font-medium text-primary">Trip Assistant</h3>}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon" aria-label={isAssistantOpen ? "Collapse Trip Assistant" : "Expand Trip Assistant"}
                      className="h-8 w-8 text-gray-500 hover:bg-gray-100 flex-shrink-0"
                      onClick={() => setIsAssistantOpen(!isAssistantOpen)}
                    >
                      {isAssistantOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                {isAssistantOpen ? (
                  <div className="flex-1 h-[calc(100%-48px)] overflow-auto">
                    <TripAssistantColumn
                      key={trip.id}
                      trip={trip}
                      components={components}
                      contacts={tripContacts}
                      onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                      }}
                      onTripUpdated={() => {
                        // Reload everything when trip details change
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
                      }}
                    />
                  </div>
                ) : (
                  <div className="p-2 text-center">
                    <MessageSquare className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                  </div>
                )}
              </div>
            )}
            
            <div className="flex-grow space-y-4">
              <div className="mb-3">
                <h2 className="text-xl font-semibold">
                  Day {visibleDayRange[0] + 1}: {format(addDays(new Date(dateRange[0]), visibleDayRange[0]), "EEEE, MMMM d, yyyy")}
                </h2>
                {/* Display the day destination */}
                {dayLocations && dayLocations[visibleDayRange[0]] && dayLocations[visibleDayRange[0]].length > 0 && (
                  <div className="flex items-center mt-1 text-sm text-gray-600">
                    <MapPinIcon className="h-4 w-4 mr-1 text-gray-500" />
                    <span>
                      {dayLocations[visibleDayRange[0]][0]}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {/* Get filtered components for this day */}
                {(() => {
                  // Filter components
                  const filteredComponents = filterComponents(groupedByDay[visibleDayRange[0]] || []);
                  
                  // Check if we have anything to display
                  if (filteredComponents.length === 0) {
                    return (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500">No activities for this day yet.</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => handleAddComponent('other', visibleDayRange[0])}
                        >
                          Add Activity
                        </Button>
                      </div>
                    );
                  }
                  
                  // Sort and render components
                  return filteredComponents
                    .sort((a, b) => {
                      if (!a.startTime) return 1;
                      if (!b.startTime) return -1;
                      return a.startTime.localeCompare(b.startTime);
                    })
                    .map((component) => (
                      <ItineraryItem
                        key={component.id}
                        component={component}
                        onClick={() => handleComponentClick(component)}
                        onDelete={() => handleDeleteComponent(component.id)}
                        onEdit={() => {
                          setSelectedComponent(component);
                          setAddComponentType(ensureComponentType(component.type));
                          setSelectedDayIndex(component.dayIndex);
                          setIsAddModalOpen(true);
                        }}
                        onRegenerate={() => {
                          setSelectedComponent(component);
                          setRegeneratePrompt(
                            `Please suggest a better ${component.type} for day ${component.dayIndex + 1} of my trip.`
                          );
                          setIsRegenerateModalOpen(true);
                        }}
                      />
                    ));
                })()}
              </div>
            </div>
          </div>
        )}
        
        {viewType === "list" && (
          <div className="flex gap-6 overflow-hidden">
            {/* Fixed AI Assistant panel on the left */}
            {trip && components && (
              <div 
                className={`h-[calc(100vh-250px)] border border-gray-200 rounded-lg transition-all duration-300 flex-shrink-0 sticky ${
                  isAssistantOpen ? "w-[350px]" : "w-[50px]"
                }`}
                style={{ maxHeight: 'calc(100vh - 200px)', marginTop: 0 }}
              >
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                      <Bot className="h-5 w-5 flex-shrink-0 text-primary" />
                      {isAssistantOpen && <h3 className="font-medium text-primary">Trip Assistant</h3>}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon" aria-label={isAssistantOpen ? "Collapse Trip Assistant" : "Expand Trip Assistant"}
                      className="h-8 w-8 text-gray-500 hover:bg-gray-100 flex-shrink-0"
                      onClick={() => setIsAssistantOpen(!isAssistantOpen)}
                    >
                      {isAssistantOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                {isAssistantOpen ? (
                  <div className="flex-1 h-[calc(100%-48px)] overflow-auto">
                    <TripAssistantColumn
                      key={trip.id}
                      trip={trip}
                      components={components}
                      contacts={tripContacts}
                      onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                      }}
                      onTripUpdated={() => {
                        // Reload everything when trip details change
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                        queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
                      }}
                    />
                  </div>
                ) : (
                  <div className="p-2 text-center">
                    <MessageSquare className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                  </div>
                )}
              </div>
            )}
            
            <div className="flex-grow space-y-4">              
              <div className="space-y-6">
                {dayCount > 0 && Array.from({ length: dayCount }, (_, dayIndex) => {
                  // Simply use the dateRange[0] directly (it's already been properly adjusted in useTrip.ts)
                  // to create a local date that matches the database calendar date
                  
                  // Add the day offset to get the specific day
                  const dayDate = addDays(dateRange[0], dayIndex);
                  
                  // Apply meal filtering to the components
                  const components = filterComponents(groupedByDay[dayIndex] || []);
                  
                  return (
                    <div key={dayIndex} id={`day-${dayIndex}`} className="border rounded-lg shadow-sm overflow-hidden">
                      <div className="bg-primary text-white p-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold">Day {dayIndex + 1}: {format(dayDate, "EEEE, MMMM d")}</h3>
                        </div>
                        
                        {/* Location indicator */}
                        {dayLocations && dayLocations[dayIndex] && dayLocations[dayIndex].length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <MapPinIcon className="h-3 w-3" />
                            <span className="text-[14px] font-medium">
                              {dayLocations[dayIndex][0]}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-3 space-y-3">
                        {components.length > 0 ? (
                          components
                            .sort((a, b) => {
                              if (!a.startTime) return 1;
                              if (!b.startTime) return -1;
                              return a.startTime.localeCompare(b.startTime);
                            })
                            .map((component) => (
                              <ItineraryItem
                                key={component.id}
                                component={component}
                                onClick={() => handleComponentClick(component)}
                                onDelete={() => handleDeleteComponent(component.id)}
                                onEdit={() => {
                                  setSelectedComponent(component);
                                  setAddComponentType(ensureComponentType(component.type));
                                  setSelectedDayIndex(component.dayIndex);
                                  setIsAddModalOpen(true);
                                }}
                                onRegenerate={() => {
                                  setSelectedComponent(component);
                                  setRegeneratePrompt(
                                    `Please suggest a better ${component.type} for day ${component.dayIndex + 1} of my trip.`
                                  );
                                  setIsRegenerateModalOpen(true);
                                }}
                              />
                            ))
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-sm text-gray-500 mb-2">No activities yet</p>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleAddComponent('other', dayIndex)}
                            >
                              Add
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Detail modal for viewing component details */}
      {selectedComponent && (
        <DetailedViewModal
          open={isDetailModalOpen}
          onOpenChange={setIsDetailModalOpen}
          component={selectedComponent}
          onDelete={() => handleDeleteComponent(selectedComponent.id)}
          onRegenerate={() => {
            setRegeneratePrompt(
              `Please suggest a better ${selectedComponent.type} for day ${selectedComponent.dayIndex + 1} of my trip.`
            );
            setIsDetailModalOpen(false);
            setIsRegenerateModalOpen(true);
          }}
          onEdit={() => {
            // Open the add component modal but pre-fill it with the current component
            setAddComponentType(ensureComponentType(selectedComponent.type));
            setSelectedDayIndex(selectedComponent.dayIndex);
            setIsDetailModalOpen(false);
            setIsAddModalOpen(true);
          }}
        />
      )}
      
      {/* Add component modal */}
      {addComponentType && (
        <AddComponentModal
          open={isAddModalOpen}
          onOpenChange={setIsAddModalOpen}
          tripId={tripId}
          type={addComponentType}
          dayIndex={selectedDayIndex !== null ? selectedDayIndex : undefined}
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          component={selectedComponent}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
            setIsAddModalOpen(false);
            toast({
              title: "Success",
              description: selectedComponent ? "Component updated successfully" : "Component added successfully",
            });
          }}
        />
      )}
      
      {/* Edit trip modal */}
      <TripFormDialog 
        open={isEditTripModalOpen}
        onOpenChange={setIsEditTripModalOpen}
        trip={trip}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
          setIsEditTripModalOpen(false);
          toast({
            title: "Success",
            description: "Trip updated successfully",
          });
        }}
      />
      
      {/* Regenerate component modal */}
      {viewType === "proposals" && (
        <div className="flex gap-6 overflow-hidden">
          {/* Fixed AI Assistant panel on the left */}
          {trip && components && (
            <div 
              className={`h-[calc(100vh-250px)] border border-gray-200 rounded-lg transition-all duration-300 flex-shrink-0 sticky ${
                isAssistantOpen ? "w-[350px]" : "w-[50px]"
              }`}
              style={{ maxHeight: 'calc(100vh - 200px)', marginTop: 0 }}
            >
              <div className="border-b border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                    <Bot className="h-5 w-5 flex-shrink-0 text-primary" />
                    {isAssistantOpen && <h3 className="font-medium text-primary">Trip Assistant</h3>}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon" aria-label={isAssistantOpen ? "Collapse Trip Assistant" : "Expand Trip Assistant"}
                    className="h-8 w-8 text-gray-500 hover:bg-gray-100 flex-shrink-0"
                    onClick={() => setIsAssistantOpen(!isAssistantOpen)}
                  >
                    {isAssistantOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Use our TripAssistantColumn component for the content */}
              <TripAssistantColumn 
                key={trip.id}
                trip={trip}
                components={components}
                contacts={tripContacts}
                onSuccess={() => refetchComponents()}
                onTripUpdated={() => refetchTrip()}
                isExpanded={isAssistantOpen}
                onAcceptAll={() => {
                  // Refresh components after accepting all
                  queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                  
                  // If trip was updated, also refresh trip data
                  queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}`] });
                  queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
                  queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/day-destinations`] });
                }}
              />
            </div>
          )}
          
          {/* Proposals content area */}
          <div className="flex-1 overflow-auto">
            <ProposalList tripId={tripId} />
          </div>
        </div>
      )}

      <Dialog open={isRegenerateModalOpen} onOpenChange={setIsRegenerateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate with AI</DialogTitle>
            <DialogDescription>
              Provide instructions for the AI to generate a replacement for this component.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="prompt">Instructions</Label>
              <Textarea
                id="prompt"
                value={regeneratePrompt}
                onChange={(e) => setRegeneratePrompt(e.target.value)}
                placeholder="Describe what you want to change or improve..."
                className="h-32"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setIsRegenerateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedComponent) {
                  handleRegenerateComponent(selectedComponent, regeneratePrompt);
                  setIsRegenerateModalOpen(false);
                }
              }}
              disabled={!regeneratePrompt.trim() || !selectedComponent}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Day destination edit modal */}
      <Dialog open={isDestinationModalOpen} onOpenChange={setIsDestinationModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDayDestination?.id 
                ? "Edit Day Destination" 
                : "Add Day Destination"}
            </DialogTitle>
            <DialogDescription>
              {editingDayDestination
                ? `Set the location for Day ${editingDayDestination.dayIndex + 1}`
                : "Set the location for this day"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="destination">Destination</Label>
              <Input
                id="destination"
                value={editingDayDestination?.destination || ""}
                onChange={(e) => setEditingDayDestination(prev => prev ? {...prev, destination: e.target.value} : null)}
                placeholder="City, Country"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={editingDayDestination?.notes || ""}
                onChange={(e) => setEditingDayDestination(prev => prev ? {...prev, notes: e.target.value} : null)}
                placeholder="Additional information about this destination..."
                className="h-24"
              />
            </div>
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-between">
            {editingDayDestination?.id && (
              <Button 
                variant="destructive"
                onClick={handleDeleteDayDestination}
              >
                Delete
              </Button>
            )}
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                onClick={() => setIsDestinationModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveDayDestination}
                disabled={!editingDayDestination?.destination}
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PDF Import Dialog */}
      <PdfImportDialog 
        tripId={tripId}
        open={isPdfImportOpen}
        onOpenChange={setIsPdfImportOpen}
        onImportComplete={() => {
          // Refresh components after import
          queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/components`] });
          
          // Show success message
          toast({
            title: "Success",
            description: "PDF itinerary has been imported successfully",
          });
        }}
        trigger={<div />}
      />
    </div>
  );
}
